import { AccessCategory, UserIntrospectionResponse } from '@/api/main/types.gen'
import { ReactElement, ReactNode, FC } from 'react'

export const hasPermission = (
  user: UserIntrospectionResponse | null | undefined,
  resource: AccessCategory,
  action: 'read' | 'write'
): boolean => {
  if (!user?.permissions?.[resource]) return false
  return user.permissions[resource][action] === true
}

export const hasAnyPermission = (
  user: UserIntrospectionResponse | null | undefined,
  resource: AccessCategory
): boolean => {
  if (!user?.permissions?.[resource]) return false
  const perms = user.permissions[resource]
  return perms.read === true || perms.write === true
}

export const hasAllPermissions = (
  user: UserIntrospectionResponse | null | undefined,
  resource: AccessCategory
): boolean => {
  if (!user?.permissions?.[resource]) return false
  const perms = user.permissions[resource]
  return perms.read === true && perms.write === true
}

/**
 * Check if user has a specific role
 */
export const hasRole = (user: UserIntrospectionResponse | null | undefined, role: string): boolean => {
  return user?.role === role
}

/**
 * Check if user is an administrator
 */
export const isAdministrator = (user: UserIntrospectionResponse | null | undefined): boolean => {
  return hasRole(user, 'administrator')
}

/**
 * Permission-based component wrapper
 * Only renders children if user has the required permission
 */
export const PermissionGuard = ({
  userPermissions,
  resource,
  action,
  requireAll = false,
  fallback = null,
  children,
}: {
  userPermissions: UserIntrospectionResponse | null | undefined
  resource: AccessCategory
  action?: 'read' | 'write'
  requireAll?: boolean
  fallback?: ReactNode
  children: ReactNode
}) => {
  if (!userPermissions) return fallback

  let hasAccess = false

  if (action) {
    hasAccess = hasPermission(userPermissions, resource, action)
  } else if (requireAll) {
    hasAccess = hasAllPermissions(userPermissions, resource)
  } else {
    hasAccess = hasAnyPermission(userPermissions, resource)
  }

  return hasAccess ? (children as ReactElement) : (fallback as ReactElement)
}

/**
 * Role-based component wrapper
 * Only renders children if user has the required role
 */
export interface RoleGuardProps {
  user: UserIntrospectionResponse | null | undefined
  role: string
  fallback?: ReactNode
  children: ReactNode
}

export const RoleGuard: FC<RoleGuardProps> = ({ user, role, fallback = null, children }) => {
  const hasAccess = hasRole(user, role)
  return hasAccess ? (children as ReactElement) : (fallback as ReactElement)
}
